/**
 * Shared contracts for the Supporter → Decision Maker outreach flow (CHANGE-95594).
 *
 * The stage machine is transcribed from the tech doc's state table (§2). Each screen is built by a
 * separate ticket against the props declared here, so this file is the seam between them — change it
 * deliberately.
 */

// The reason ids, the draft source and `seedDraft` live in `shared/utils/contactDecisionMaker` so the
// hooks layer can use them too — it is not allowed to import `shared/components`. Re-exported here so
// the components layer still has one place to import the flow's vocabulary from.
import type {
	ContactDecisionMakerDraftSource,
	ContactDecisionMakerReasonId,
	ContactDecisionMakerSendFailure,
} from '#src/app/shared/utils/contactDecisionMaker';

export type { ContactDecisionMakerDraftSource, ContactDecisionMakerReasonId, ContactDecisionMakerSendFailure };

/**
 * Stages where none of the flow is on screen. Both a guard and a definition: `open` is only accepted
 * from one of these, and focus restoration keys on leaving them. Shared so the reducer and the flow
 * renderer cannot disagree about what "closed" means.
 */
const CONTACT_DECISION_MAKER_CLOSED_STAGES = ['idle', 'contacted'] as const;

/** Whether any of the flow is on screen — the inverse of being in one of the closed stages. */
export function isContactDecisionMakerFlowOnScreen(stage: ContactDecisionMakerStage): boolean {
	return !(CONTACT_DECISION_MAKER_CLOSED_STAGES as readonly ContactDecisionMakerStage[]).includes(stage);
}

export type ContactDecisionMakerStage =
	| 'idle'
	| 'story:pick'
	| 'story:write'
	| 'drafting'
	| 'preview'
	| 'preview:editing'
	| 'sending'
	| 'success'
	| 'share'
	| 'contacted';

export type ContactDecisionMakerFlowState = {
	stage: ContactDecisionMakerStage;
	/** Set from `story:write` onwards. */
	reasonId: ContactDecisionMakerReasonId | undefined;
	/** The supporter's own words. Survives every later stage — it is what the draft is built from. */
	story: string;
	/** The message as it will be sent, including any edits made in `preview:editing`. */
	draft: string;
	draftSource: ContactDecisionMakerDraftSource | undefined;
	/**
	 * Set when a send failure returned the supporter to `preview`; cleared by the next `send` and by
	 * editing the draft. The reason itself stays with the caller's mutation state — the machine only
	 * needs to know whether a retry is being offered or the message has to change first.
	 */
	sendFailure: ContactDecisionMakerSendFailure | undefined;
};

export type ContactDecisionMakerFlowActions = {
	/** Entry surfaces call this; opens at `story:pick`. */
	open: () => void;
	/** Any dismissal path — close button, ESC, outside click. Returns to `idle`. */
	dismiss: () => void;
	pickReason: (reasonId: ContactDecisionMakerReasonId) => void;
	/** `story:write` → `story:pick`. */
	backToReasons: () => void;
	setStory: (story: string) => void;
	/** "Draft my message" — moves to `drafting` while the mutation is in flight. */
	requestDraft: () => void;
	draftReady: (draft: string, source: ContactDecisionMakerDraftSource) => void;
	/**
	 * "Continue with my own words" — skips drafting and carries the supporter's own words into the
	 * read-only review, the same screen a generated draft lands on. Seeding is the caller's job because
	 * it needs the DM's name and i18n.
	 */
	writeOwnDraft: (seededDraft: string) => void;
	/** Tapping the draft card. */
	editDraft: () => void;
	setDraft: (draft: string) => void;
	/** "Done" in the editor. */
	finishEditing: () => void;
	/** "Send to {DM}". */
	send: () => void;
	/**
	 * Returns to `preview` rather than dead-ending. `retryable` decides whether the review screen offers
	 * to send the same message again or asks for it to be edited first.
	 */
	sendFailed: (failure: ContactDecisionMakerSendFailure) => void;
	sendSucceeded: () => void;
	openShare: () => void;
	closeShare: () => void;
	/** "Back to the petition" — leaves the flow in its contacted state. */
	finish: () => void;
};

/** What `useContactDecisionMakerFlow` returns; named `…Model` so the component keeps the plain name. */
export type ContactDecisionMakerFlowModel = ModelHookResult<
	ContactDecisionMakerFlowState,
	ContactDecisionMakerFlowActions
>;

/**
 * The decision maker as every stage needs it — deliberately flat, so the stages do not depend on
 * either page's query shape.
 *
 * It is NOT what the page queries return. SPH and PSF both select nested objects
 * (`photo { userMedium { url } }`, `location { displayName }`), so whatever mounts the flow maps
 * those down to `photo` and `location` strings. Keep that mapping at the mount point.
 */
export type ContactDecisionMakerTarget = {
	id: string;
	displayName: string;
	title?: string | null;
	photo?: string | null;
	location?: string | null;
};

/** Props every stage component receives from the flow renderer. */
export type ContactDecisionMakerStageProps = {
	decisionMaker: ContactDecisionMakerTarget;
	petitionId: string;
	/** Interpolated into the "Something else" placeholder, which has no prompt to lean on. */
	petitionTitle: string;
	flow: ContactDecisionMakerFlowModel;
	/**
	 * Element focus returns to when the flow is dismissed. The sheet opens programmatically, so
	 * react-aria has no trigger to restore to and focus would otherwise fall to `<body>`
	 * (WCAG 2.4.3, cf. CHANGE-96802).
	 */
	restoreFocusRef: React.RefObject<HTMLElement | null>;
};

export type ContactDecisionMakerSuccessStageProps = ContactDecisionMakerStageProps & {
	/**
	 * Attach to the success heading. The share sheet layers over this screen, so dismissing the sheet
	 * has to return focus here rather than to `restoreFocusRef` — that one points at the entry CTA,
	 * which sits behind the overlay.
	 */
	headingRef: React.RefObject<HTMLHeadingElement | null>;
	/**
	 * Whether the share sheet's links are still being fetched. The share CTA waits on this, so the
	 * sheet never opens with nothing in it.
	 */
	isShareLoading: boolean;
};
